import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useState } from 'react';

import { V1Amount } from '@/api/clients/ohip';
import { Box, Flex } from '@/box';
import { useCurrencyPreview } from '@/currency-preview';
import { useTranslation } from '@/i18n';
import { Markdown } from '@/markdown';
import { BaseModal, Button } from '@/ui/controls';
import { Checkbox } from '@/ui/form';
import { Card, Divider } from '@/ui/layout';
import { useTheme } from '@/ui/theme';
import { BodyCopy, Caption, Text } from '@/ui/typography';

import { MembershipBenefitsModal } from './MembershipBenefitsModal';
import { MembershipSaving } from './MembershipSaving';

export const MembershipCard = (props: {
  membershipPrice: V1Amount;
  amountSaved: V1Amount;
}) => {
  const [isChecked, setIsChecked] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMembershipBenefitsModalOpen, setIsMembershipBenefitsModalOpen] =
    useState(false);
  const router = useRouter();
  const { componentProperties, keylines } = useTheme();
  const { t } = useTranslation('bookingMembership');

  const price = useCurrencyPreview({ amount: props.membershipPrice });
  const saving = useCurrencyPreview({ amount: props.amountSaved });

  const backToPreviousPage = () => {
    router.back();
  };

  return (
    <Card
      p="m"
      css={{
        border: keylines?.border,
        backgroundColor: componentProperties?.addOnCard?.backgroundColor,
        borderRadius: componentProperties?.addOnCard?.borderRadius,
      }}
    >
      <Flex flexDirection={['column', 'row']} gap={24}>
        <Flex
          flex={2 / 5}
          justifyContent="center"
          alignItems="flex-start"
          height="fit-content"
          backgroundColor={'#143C21'}
          padding={['25px 0', '18px 0']}
          borderRadius="4px"
        >
          <Box
            css={{
              '@media (max-width: 639px)': {
                '& img': {
                  width: '119px',
                  height: '134px',
                },
              },
            }}
          >
            <Image
              src={'/disloyalty/disloyalty-logo-white.svg'}
              alt={'disloyalty logo'}
              width={89}
              height={99}
            />
          </Box>
        </Flex>
        <Flex flex={3 / 5} flexDirection="column" gap={16}>
          <Text style="heading6">{t('card.title')}</Text>
          <MembershipSaving saving={saving} />
          <BodyCopy>
            <Markdown source={t('card.description', { price })} />
          </BodyCopy>
          <Caption css={{ fontWeight: 'bold' }}>
            <a
              onClick={() => setIsMembershipBenefitsModalOpen(true)}
              rel="noreferrer"
              style={{ cursor: 'pointer' }}
            >
              {t('benefits')}
            </a>
          </Caption>
          <Divider css={{ borderTop: '1px solid #E6D0B9' }} />
          <Checkbox
            name="membership"
            label={t('card.checkboxLabel', { price })}
            isChecked={isChecked}
            onChange={() =>
              isChecked ? setIsModalOpen(true) : setIsChecked(false)
            }
          />
        </Flex>
      </Flex>
      <BaseModal
        isOpen={isModalOpen}
        onRequestClose={() => {
          setIsModalOpen(false);
        }}
        centered
      >
        <Card
          rounded
          css={{
            padding: '80px 124px',
            '@media (max-width: 639px)': {
              padding: '16px',
            },
            textAlign: 'center',
            maxWidth: '650px',
            border: 'none',
          }}
        >
          <Flex flexDirection="column" gap={40}>
            <Text style="heading5">
              <Markdown
                // Make the word Dis-loyalty to not break the line
                source={t('modal.title').replace(
                  'Dis-loyalty',
                  'Dis&#8209;loyalty'
                )}
              />
            </Text>
            <Text style="subheader3" css={{ fontWeight: 'bold' }}>
              {t('saving', { saving })}
            </Text>
            <Button
              onClick={() => setIsModalOpen(false)}
              css={{ marginBottom: '-20px' }}
            >
              {t('modal.confirmButton')}
            </Button>
            <Button onClick={backToPreviousPage} variant="secondary">
              {t('modal.cancelButton')}
            </Button>
          </Flex>
        </Card>
      </BaseModal>
      <MembershipBenefitsModal
        isOpen={isMembershipBenefitsModalOpen}
        onClose={() => setIsMembershipBenefitsModalOpen(false)}
      />
    </Card>
  );
};
